/**
 * Types and utilities for Attack Paths Wizard Modals
 *
 * Provides form data interfaces, input sanitization, and validation
 * functions for the three wizard types: Asset, Vulnerability, and Technique.
 */

import sanitizeHtml from 'sanitize-html';

import mitreData from '@/sections/attacks/attackPaths/data.json';
import type {
  CustomAssetNode,
  CustomTechniqueNode,
  CustomVulnerabilityNode,
  TechniqueStatus,
} from '@/sections/attacks/attackPaths/types';
import { migrateLegacyIcon } from '@/sections/attacks/attackPaths/utils';

// Flatten all techniques from MITRE data for lookups
const allTechniques = mitreData.flatMap(tactic => tactic.techniques);

// ============================================================================
// Form Data Interfaces
// ============================================================================

/**
 * Form data for Asset wizard (2 steps: Search → Icon)
 */
export interface AssetWizardFormData {
  /** Selected asset key or custom asset name */
  assetId: string;
  /** Human-readable display name for the asset (e.g., "mlb.praetorian.com" instead of "#asset#mlb.praetorian.com#104.154.50.197") */
  assetDisplayName?: string;
  /** IP address or group for secondary display (e.g., "104.154.50.197") */
  assetMetadata?: string;
  /** Whether the asset is custom (non-Chariot) */
  isCustom: boolean;
  /** Selected icon override (Lucide icon name) */
  iconOverride?: string;
}

/**
 * Form data for Vulnerability wizard (2 steps: Search → Asset Selection)
 */
export interface VulnerabilityWizardFormData {
  /** Selected vulnerability key or custom name */
  vulnerabilityKey: string;
  /** Custom display name for the vulnerability */
  vulnerabilityName?: string;
  /** Associated asset for display (e.g., "sensor-windows-2019") */
  vulnerabilityMetadata?: string;
  /** Priority/severity (0=Critical, 10=High, 20=Medium, 30=Low, 40=Info, 50=Exposure) */
  priority?: number;
  /** Whether the vulnerability is custom (non-Chariot) */
  isCustom: boolean;
  /** Affected asset key or custom name */
  affectedAssetId: string;
  /** Whether the affected asset is custom */
  isAffectedAssetCustom: boolean;
}

/**
 * Form data for Technique wizard (2 steps: Search → Status)
 */
export interface TechniqueWizardFormData {
  /** MITRE ATT&CK technique ID (e.g., T1059) */
  techniqueId: string;
  /** Technique name from MITRE data */
  techniqueName: string;
  /** Custom technique name (if not using MITRE technique) */
  customName?: string;
  /** Defense/detection status */
  status?: TechniqueStatus;
}

// ============================================================================
// Input Sanitization
// ============================================================================

/**
 * Maximum length for user input strings
 */
export const MAX_INPUT_LENGTH = 255;

/**
 * Maximum length for technique IDs (e.g., T1059.001)
 */
export const MAX_TECHNIQUE_ID_LENGTH = 50;

/**
 * Sanitizes user input to prevent XSS attacks.
 *
 * Strips all HTML tags and trims whitespace. Enforces maximum length
 * to prevent DoS via extremely long strings.
 *
 * @param input - Raw user input string
 * @param maxLength - Maximum allowed length (default: 255)
 * @returns Sanitized string safe for rendering
 *
 * @example
 * sanitizeInput('<script>alert("xss")</script>Hello') // 'Hello'
 * sanitizeInput('  Normal text  ') // 'Normal text'
 * sanitizeInput('a'.repeat(1000)) // First 255 characters
 */
export function sanitizeInput(
  input: string,
  maxLength: number = MAX_INPUT_LENGTH
): string {
  if (typeof input !== 'string') {
    return '';
  }

  // Strip all HTML tags using sanitize-html with no allowed tags
  const sanitized = sanitizeHtml(input, {
    allowedTags: [],
    allowedAttributes: {},
  });

  // Trim whitespace and enforce length limit
  return sanitized.trim().slice(0, maxLength);
}

// ============================================================================
// Validators
// ============================================================================

/**
 * Validates that a required string field has content within bounds.
 *
 * @param value - String value to validate
 * @param maxLength - Maximum allowed length (default: 255)
 * @returns true if valid, false otherwise
 */
export function isRequiredString(
  value: string,
  maxLength: number = MAX_INPUT_LENGTH
): boolean {
  return value.length > 0 && value.length <= maxLength;
}

/**
 * Validates Asset wizard form data for step 1 (asset selection).
 *
 * @param data - Asset wizard form data
 * @returns true if asset is selected/entered
 */
export function validateAssetStep(data: AssetWizardFormData): boolean {
  return isRequiredString(data.assetId);
}

/**
 * Validates Asset wizard form data for step 2 (icon selection).
 * Icon selection is required.
 *
 * @param data - Asset wizard form data
 * @returns true if an icon is selected
 */
export function validateIconStep(data: AssetWizardFormData): boolean {
  return data.iconOverride !== undefined && data.iconOverride.length > 0;
}

/**
 * Validates Vulnerability wizard form data for step 1 (vulnerability selection).
 *
 * @param data - Vulnerability wizard form data
 * @returns true if vulnerability is selected/entered
 */
export function validateVulnerabilityStep(
  data: VulnerabilityWizardFormData
): boolean {
  return isRequiredString(data.vulnerabilityKey);
}

/**
 * Validates Vulnerability wizard form data for step 2 (affected asset selection).
 *
 * @param data - Vulnerability wizard form data
 * @returns true if affected asset is selected/entered
 */
export function validateAffectedAssetStep(
  data: VulnerabilityWizardFormData
): boolean {
  return isRequiredString(data.affectedAssetId);
}

/**
 * Validates Technique wizard form data for step 1 (technique selection).
 *
 * @param data - Technique wizard form data
 * @returns true if technique ID or custom name is provided
 */
export function validateTechniqueStep(data: TechniqueWizardFormData): boolean {
  // Either a MITRE technique ID or custom name must be provided
  const hasTechniqueId =
    data.techniqueId.length > 0 &&
    data.techniqueId.length <= MAX_TECHNIQUE_ID_LENGTH;
  const hasCustomName =
    !!data.customName &&
    data.customName.length > 0 &&
    data.customName.length <= MAX_INPUT_LENGTH;

  return hasTechniqueId || hasCustomName;
}

/**
 * Validates Technique wizard form data for step 2 (status selection).
 * User must select a status for the technique.
 *
 * @param data - Technique wizard form data
 * @returns true if status is selected
 */
export function validateStatusStep(data: TechniqueWizardFormData): boolean {
  return data.status !== undefined;
}

// ============================================================================
// Default Values
// ============================================================================

/**
 * Creates initial form data for Asset wizard.
 */
export function createAssetFormData(): AssetWizardFormData {
  return {
    assetId: '',
    assetDisplayName: '',
    assetMetadata: undefined,
    isCustom: false,
    iconOverride: undefined,
  };
}

/**
 * Creates initial form data for Vulnerability wizard.
 */
export function createVulnerabilityFormData(): VulnerabilityWizardFormData {
  return {
    vulnerabilityKey: '',
    vulnerabilityName: undefined,
    vulnerabilityMetadata: undefined,
    isCustom: false,
    affectedAssetId: '',
    isAffectedAssetCustom: false,
  };
}

/**
 * Creates initial form data for Technique wizard.
 */
export function createTechniqueFormData(): TechniqueWizardFormData {
  return {
    techniqueId: '',
    techniqueName: '',
    customName: undefined,
    status: undefined,
  };
}

// ============================================================================
// From Node Factory Functions (for Edit Mode)
// ============================================================================

/**
 * Parse asset key to extract human-readable display name and metadata.
 * Handles all entity types: #asset#, #aduser#, #adcomputer#, etc.
 *
 * Key format: #<entityType>#<group/domain>#<identifier>
 *
 * Returns identifier as displayName (primary) and group as metadata (secondary)
 * to match the table display pattern where identifier is shown prominently.
 *
 * @param key - Asset key string (e.g., "#aduser#wraith.corp#S-1-5-21-...")
 * @returns Object with displayName (identifier) and optional metadata (group)
 *
 * @example
 * parseAssetKey('#asset#mlb.praetorian.com#104.154.50.197')
 * // returns { displayName: '104.154.50.197', metadata: 'mlb.praetorian.com' }
 *
 * parseAssetKey('#aduser#wraith.corp#S-1-5-21-123')
 * // returns { displayName: 'S-1-5-21-123', metadata: 'wraith.corp' }
 */
function parseAssetKey(key: string): {
  displayName: string;
  metadata?: string;
} {
  // Key format: #<entityType>#<group/domain>#<identifier>
  const parts = key.split('#').filter(Boolean);

  // Handle any entity type with 3+ parts (asset, aduser, adcomputer, etc.)
  if (parts.length >= 3) {
    return {
      displayName: parts[2], // identifier (e.g., IP address, SID)
      metadata: parts[1], // group/domain
    };
  }

  if (parts.length === 2) {
    return { displayName: parts[1] };
  }

  // Fallback for unexpected format
  return { displayName: key };
}

/**
 * Creates AssetWizardFormData from an existing node for edit mode.
 *
 * Uses stored displayName/metadata if available (new nodes), otherwise
 * falls back to parsing the key (legacy nodes).
 *
 * @param node - Existing asset node to extract data from
 * @returns Form data pre-populated with node values
 *
 * @example
 * ```tsx
 * const initialData = assetFormDataFromNode(existingAssetNode);
 * // Use in wizard with mode="edit"
 * ```
 */
export function assetFormDataFromNode(
  node: CustomAssetNode
): AssetWizardFormData {
  const isCustom = node.data.type === 'nonChariot';

  // Use stored display name if available (new nodes)
  let assetDisplayName = node.data.displayName || '';
  let assetMetadata = node.data.metadata;

  // Fall back to parsing key if displayName not stored (legacy nodes)
  if (!assetDisplayName && node.data.id) {
    if (isCustom) {
      // For custom assets, use id directly as display name
      assetDisplayName = node.data.id;
    } else {
      // For Chariot assets, parse the key
      const parsed = parseAssetKey(node.data.id);
      assetDisplayName = parsed.displayName;
      assetMetadata = assetMetadata ?? parsed.metadata;
    }
  }

  return {
    assetId: node.data.id || '',
    assetDisplayName,
    assetMetadata,
    isCustom,
    iconOverride: migrateLegacyIcon(node.data.iconOverride),
  };
}

/**
 * Creates TechniqueWizardFormData from an existing node for edit mode.
 *
 * @param node - Existing technique node to extract data from
 * @returns Form data pre-populated with node values
 *
 * @example
 * ```tsx
 * const initialData = techniqueFormDataFromNode(existingTechniqueNode);
 * // Use in wizard with mode="edit"
 * ```
 */
export function techniqueFormDataFromNode(
  node: CustomTechniqueNode
): TechniqueWizardFormData {
  // MITRE technique IDs start with T (e.g., T1059)
  const isMitreTechnique = node.data.id?.startsWith('T') ?? false;

  // Look up technique name from MITRE data if not stored on node
  let techniqueName = node.data.name || '';
  if (!techniqueName && isMitreTechnique && node.data.id) {
    const mitreTechnique = allTechniques.find(t => t.id === node.data.id);
    techniqueName = mitreTechnique?.name || '';
  }

  return {
    techniqueId: isMitreTechnique ? node.data.id || '' : '',
    techniqueName: isMitreTechnique ? techniqueName : '',
    customName: !isMitreTechnique ? node.data.name : undefined,
    status: node.data.status || 'untested',
  };
}

/**
 * Creates VulnerabilityWizardFormData from an existing node for edit mode.
 *
 * @param node - Existing vulnerability node to extract data from
 * @returns Form data pre-populated with node values
 *
 * @example
 * ```tsx
 * const initialData = vulnerabilityFormDataFromNode(existingVulnerabilityNode);
 * // Use in wizard with mode="edit"
 * ```
 */
export function vulnerabilityFormDataFromNode(
  node: CustomVulnerabilityNode
): VulnerabilityWizardFormData {
  const isCustom = node.data.type === 'nonChariot';
  return {
    vulnerabilityKey: node.data.key || '',
    vulnerabilityName: node.data.name,
    priority: node.data.priority,
    isCustom,
    // Affected asset is not editable in this flow
    affectedAssetId: '',
    isAffectedAssetCustom: false,
  };
}
